import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const CommonRangeFilter = ({
  open,
  onClose,
  filterConfig,
  onApplyFilter,
}) => {
  const [filters, setFilters] = useState(() => {
    const initialFilters = {};
    filterConfig.forEach((config) => {
      if (config.type === "dateRange") {
        initialFilters[`${config.key}From`] = "";
        initialFilters[`${config.key}To`] = "";
      } else {
        initialFilters[config.key] = "";
      }
    });
    return initialFilters;
  });

  const [options, setOptions] = useState({});
  const [autocompleteOpen, setAutocompleteOpen] = useState({});
  const [filteredOptions, setFilteredOptions] = useState({});

  useEffect(() => {
    const fetchOptions = async () => {
      const newOptions = {};

      for (const config of filterConfig) {
        if (
          (config.type === "select" || config.type === "autocomplete") &&
          config.options
        ) {
          newOptions[config.key] = config.options;
        }
      }

      setOptions(newOptions);
      setFilteredOptions(newOptions);
    };

    if (open) fetchOptions();
  }, [open, filterConfig]);

  if (!open) return null;

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleAutocompleteChange = (key, value) => {
    handleFilterChange(key, value);
    
    // Filter options based on input
    if (options[key]) {
      const filtered = options[key].filter(option =>
        option.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(prev => ({ ...prev, [key]: filtered }));
    }
    
    // Open dropdown if there's input
    setAutocompleteOpen(prev => ({ ...prev, [key]: value.length > 0 }));
  };

  const handleAutocompleteSelect = (key, value) => {
    handleFilterChange(key, value);
    setAutocompleteOpen(prev => ({ ...prev, [key]: false }));
  };

  const buildFilterPayload = () => {
    const payload = {};

    filterConfig.forEach((config) => {
      if (
        config.type === "select" ||
        config.type === "autocomplete"
      ) {
        const value = filters[config.key];
        if (value) payload[config.key] = value;
      }

      if (config.type === "dateRange") {
        const fromValue = filters[`${config.key}From`];
        const toValue = filters[`${config.key}To`];

        if (fromValue) payload[`${config.key}From`] = fromValue;
        if (toValue) payload[`${config.key}To`] = toValue;
      }
    });

    return payload;
  };

  const handleApply = () => {
    onApplyFilter(buildFilterPayload());
    onClose();
  };

  const handleReset = () => {
    const resetValues = {};
    filterConfig.forEach((config) => {
      if (config.type === "dateRange") {
        resetValues[`${config.key}From`] = "";
        resetValues[`${config.key}To`] = "";
      } else {
        resetValues[config.key] = "";
      }
    });
    setFilters(resetValues);
    setAutocompleteOpen({});
    onApplyFilter({});
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "transparent",
        zIndex: 1400,
      }}
    >
      <div
        style={{
          position: "fixed",
          top: "80px",
          right: "40px",
          width: "380px",
          maxHeight: "calc(100vh - 120px)",
          backgroundColor: "#fff",
          border: "none",
          borderRadius: "20px",
          padding: "0",
          zIndex: 1500,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1)",
          fontFamily: "'Inter', 'Segoe UI', sans-serif",
          display: "flex",
          flexDirection: "column",
          animation: "slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            padding: "24px 28px",
            background: "linear-gradient(135deg, #4151e0, #4151e0)",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
              }}
            >
              🔍
            </div>
            <h3
              style={{
                margin: 0,
                color: "#fff",
                fontSize: "19px",
                fontWeight: "600",
                letterSpacing: "-0.3px",
              }}
            >
              Filter Inventory
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "#fff",
              fontSize: "22px",
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "300",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255,255,255,0.3)";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255,255,255,0.2)";
              e.target.style.transform = "scale(1)";
            }}
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div
          style={{
            padding: "28px",
            overflowY: "auto",
            flex: 1,
            scrollbarWidth: "thin",
            scrollbarColor: "#e0e0e0 transparent",
          }}
        >
          {filterConfig.map((field, index) => (
            <div
              key={field.key}
              style={{
                marginBottom: index === filterConfig.length - 1 ? "0" : "24px",
                position: "relative",
              }}
            >
              <label
                style={{
                  fontWeight: "600",
                  marginBottom: "10px",
                  display: "block",
                  color: "#1a1a1a",
                  fontSize: "13px",
                  letterSpacing: "0.3px",
                  textTransform: "uppercase",
                }}
              >
                {field.label}
              </label>

              {/* AUTOCOMPLETE */}
              {field.type === "autocomplete" && (
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#999",
                      fontSize: "18px",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  >
                    🔎
                  </span>
                  <input
                    type="text"
                    value={filters[field.key] || ""}
                    onChange={(e) =>
                      handleAutocompleteChange(field.key, e.target.value)
                    }
                    onFocus={() => {
                      if (options[field.key]?.length > 0) {
                        setAutocompleteOpen(prev => ({ ...prev, [field.key]: true }));
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setAutocompleteOpen(prev => ({ ...prev, [field.key]: false }));
                      }, 200);
                    }}
                    placeholder={`Search ${field.label}...`}
                    style={{
                      width: "100%",
                      padding: "13px 16px 13px 44px",
                      borderRadius: "12px",
                      border: "2px solid #e8e8e8",
                      fontSize: "14px",
                      backgroundColor: "#fafafa",
                      outline: "none",
                      transition: "all 0.3s ease",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                    onFocusCapture={(e) => {
                      e.target.style.borderColor = "#4151e0";
                      e.target.style.backgroundColor = "#fff";
                      e.target.style.boxShadow = "0 0 0 4px rgba(65,81,224,0.08)";
                    }}
                    onBlurCapture={(e) => {
                      setTimeout(() => {
                        e.target.style.borderColor = "#e8e8e8";
                        e.target.style.backgroundColor = "#fafafa";
                        e.target.style.boxShadow = "none";
                      }, 200);
                    }}
                  />
                  
                  {/* Dropdown Options */}
                  {autocompleteOpen[field.key] && filteredOptions[field.key]?.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        backgroundColor: "#fff",
                        border: "2px solid #e8e8e8",
                        borderRadius: "12px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        zIndex: 1000,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      }}
                    >
                      {filteredOptions[field.key].map((option, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleAutocompleteSelect(field.key, option)}
                          style={{
                            padding: "12px 16px",
                            cursor: "pointer",
                            fontSize: "14px",
                            transition: "all 0.2s ease",
                            borderBottom: idx !== filteredOptions[field.key].length - 1 ? "1px solid #f5f5f5" : "none",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#f8f9ff";
                            e.target.style.color = "#4151e0";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent";
                            e.target.style.color = "inherit";
                          }}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* SELECT */}
              {field.type === "select" && (
                <select
                  value={filters[field.key] || ""}
                  onChange={(e) =>
                    handleFilterChange(field.key, e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "13px 16px",
                    borderRadius: "12px",
                    border: "2px solid #e8e8e8",
                    fontSize: "14px",
                    backgroundColor: "#fafafa",
                    cursor: "pointer",
                    outline: "none",
                    transition: "all 0.3s ease",
                    fontFamily: "inherit",
                    appearance: "none",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 16px center",
                    paddingRight: "40px",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#4151e0";
                    e.target.style.backgroundColor = "#fff";
                    e.target.style.boxShadow = "0 0 0 4px rgba(65,81,224,0.08)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e8e8e8";
                    e.target.style.backgroundColor = "#fafafa";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <option value="">Select {field.label}...</option>
                  {options[field.key]?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}

              {/* DATE RANGE */}
              {field.type === "dateRange" && (
                <div style={{ display: "flex", gap: "12px" }}>
                  <input
                    type="date"
                    value={filters[`${field.key}From`] || ""}
                    onChange={(e) =>
                      handleFilterChange(
                        `${field.key}From`,
                        e.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      padding: "13px 12px",
                      borderRadius: "12px",
                      border: "2px solid #e8e8e8",
                      backgroundColor: "#fafafa",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.3s ease",
                      fontFamily: "inherit",
                      cursor: "pointer",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#4151e0";
                      e.target.style.backgroundColor = "#fff";
                      e.target.style.boxShadow = "0 0 0 4px rgba(65,81,224,0.08)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e8e8e8";
                      e.target.style.backgroundColor = "#fafafa";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <input
                    type="date"
                    value={filters[`${field.key}To`] || ""}
                    onChange={(e) =>
                      handleFilterChange(
                        `${field.key}To`,
                        e.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      padding: "13px 12px",
                      borderRadius: "12px",
                      border: "2px solid #e8e8e8",
                      backgroundColor: "#fafafa",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.3s ease",
                      fontFamily: "inherit",
                      cursor: "pointer",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#4151e0";
                      e.target.style.backgroundColor = "#fff";
                      e.target.style.boxShadow = "0 0 0 4px rgba(65,81,224,0.08)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e8e8e8";
                      e.target.style.backgroundColor = "#fafafa";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div
          style={{
            padding: "20px 28px 28px",
            display: "flex",
            gap: "12px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <button
            onClick={handleReset}
            style={{
              flex: 1,
              backgroundColor: "#f5f5f5",
              border: "2px solid #e8e8e8",
              color: "#666",
              padding: "14px 20px",
              borderRadius: "12px",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#eeeeee";
              e.target.style.borderColor = "#d8d8d8";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#f5f5f5";
              e.target.style.borderColor = "#e8e8e8";
              e.target.style.transform = "translateY(0)";
            }}
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            style={{
              flex: 1,
              background: "linear-gradient(135deg, #4151e0, #4151e0)",
              color: "#fff",
              border: "none",
              padding: "14px 20px",
              borderRadius: "12px",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
              boxShadow: "0 4px 14px rgba(65,81,224,0.3)",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 20px rgba(65,81,224,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 14px rgba(65,81,224,0.3)";
            }}
          >
            Apply Filters
          </button>
        </div>

        <style>
          {`
            @keyframes slideIn {
              from { 
                opacity: 0; 
                transform: translateX(30px) scale(0.95); 
              }
              to { 
                opacity: 1; 
                transform: translateX(0) scale(1); 
              }
            }

            ::-webkit-scrollbar {
              width: 6px;
            }

            ::-webkit-scrollbar-track {
              background: transparent;
            }

            ::-webkit-scrollbar-thumb {
              background: #e0e0e0;
              border-radius: 10px;
            }

            ::-webkit-scrollbar-thumb:hover {
              background: #d0d0d0;
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default CommonRangeFilter;